import type { LinkExitError } from '@moneykit/connect';
import { datadogRum } from '@datadog/browser-rum';

export type MoneykitConnectFlow =
  | 'moneykit-connect'
  | 'moneykit-relink'
  | 'moneykit-connect-oauth'
  | 'moneykit-relink-oauth';

export type MoneykitConnectFailure = {
  readonly errorId: string | null;
  readonly displayedMessage: string | null;
  readonly requestId: string | null;
  readonly linkSessionId: string | null;
  readonly linkType: string | null;
  readonly institutionName: string | null;
};

const FALLBACK_MESSAGE = "Couldn't connect your bank. Please try again, or contact support if it keeps happening.";
const CANCEL_MESSAGE = 'Connection cancelled.';

function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStringField(value: unknown, key: string) {
  if (!isJsonRecord(value)) return null;
  const field = value[key];
  return typeof field === 'string' ? field : null;
}

function nonBlank(value: string | null) {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

// Connect draws its own failure screen inside the cross-origin iframe, so the
// only trace of a vendor-side failure that ever reaches us is the `error` link
// event. `error_id` names the failure class (docs.moneykit.com/connect/errors)
// and `request_id` is the sole handle MoneyKit support can trace a ticket by;
// both are gone once the iframe is torn down.
export function readMoneykitConnectEventFailure(event: unknown) {
  if (!isJsonRecord(event)) return null;
  if (event['name'] !== 'error') return null;

  const properties = event['properties'];
  const meta = event['meta'];
  const failure: MoneykitConnectFailure = {
    errorId: readStringField(properties, 'error_id'),
    displayedMessage: readStringField(properties, 'displayed_message'),
    requestId: readStringField(properties, 'request_id'),
    linkSessionId: readStringField(meta, 'link_session_id'),
    linkType: readStringField(meta, 'link_type'),
    institutionName: readStringField(meta, 'institution_name'),
  };
  return failure;
}

// The exit callback carries a narrower payload than the `error` event — no
// session or institution — so it is the fallback for a failure that ends the
// flow without emitting an event first.
export function moneykitConnectFailureFromExit(error: LinkExitError) {
  const failure: MoneykitConnectFailure = {
    errorId: error.error_id,
    displayedMessage: error.displayed_message,
    requestId: error.request_id,
    linkSessionId: null,
    linkType: null,
    institutionName: null,
  };
  return failure;
}

export function mergeMoneykitConnectFailure(reported: MoneykitConnectFailure | null, exit: LinkExitError | null) {
  if (exit === null) return reported;
  if (reported === null) return moneykitConnectFailureFromExit(exit);

  const fromExit = moneykitConnectFailureFromExit(exit);
  const failure: MoneykitConnectFailure = {
    errorId: reported.errorId ?? fromExit.errorId,
    displayedMessage: nonBlank(reported.displayedMessage) ?? fromExit.displayedMessage,
    requestId: reported.requestId ?? fromExit.requestId,
    linkSessionId: reported.linkSessionId ?? fromExit.linkSessionId,
    linkType: reported.linkType ?? fromExit.linkType,
    institutionName: reported.institutionName ?? fromExit.institutionName,
  };
  return failure;
}

type MoneykitConnectFailureMessageParams = {
  readonly failure: MoneykitConnectFailure;
  readonly preferCancelWhenBlank?: boolean;
};

export function moneykitConnectFailureMessage({
  failure,
  preferCancelWhenBlank = false,
}: MoneykitConnectFailureMessageParams) {
  const displayed = nonBlank(failure.displayedMessage);
  if (displayed !== null) return displayed;
  if (preferCancelWhenBlank) return CANCEL_MESSAGE;
  return FALLBACK_MESSAGE;
}

type ReportMoneykitConnectFailureParams = {
  readonly failure: MoneykitConnectFailure;
  readonly flow: MoneykitConnectFlow;
};

export function reportMoneykitConnectFailure({ failure, flow }: ReportMoneykitConnectFailureParams) {
  try {
    datadogRum.addError(`MoneyKit Connect failed: ${failure.errorId ?? 'unknown'}`, {
      feature: 'moneykit-connect',
      flow,
      moneykitErrorId: failure.errorId,
      moneykitRequestId: failure.requestId,
      moneykitLinkSessionId: failure.linkSessionId,
      moneykitLinkType: failure.linkType,
    });
  } catch (error: unknown) {
    console.error(error);
  }
}
